import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import Icon from '../shared/Icon';

const FONT = "'abcNormal','Inter','Arial',sans-serif";
const FONT_UI = "'Inter',system-ui,sans-serif";

const FONT_FACE = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  .ci-btn{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border:1px solid #E2E8F0;border-radius:8px;background:#fff;color:#1E293B;font-size:12.5px;font-weight:500;cursor:pointer;transition:background .15s,border-color .15s;font-family:${FONT_UI};}
  .ci-btn:hover{background:#F8FAFC;border-color:#CBD5E1;}
  .ci-row{cursor:pointer;transition:background .12s;}
  .ci-row:hover{background:#F8FAFC;}
  .ci-input{border:1px solid #E2E8F0;border-radius:8px;padding:8px 12px;font-size:13px;font-family:${FONT_UI};color:#1E293B;outline:none;background:#fff;}
  .ci-input:focus{border-color:#004AAD;}
  .ci-filter-label{font-size:10.5px;font-weight:600;color:#94A3B8;text-transform:uppercase;letter-spacing:.04em;font-family:${FONT_UI};margin-bottom:4px;display:block;}
  .ci-card{background:#fff;border:1px solid #F1F5F9;border-radius:14px;box-shadow:0 1px 2px rgba(15,23,42,0.03);}
`;

// One color+icon per metric, reused consistently everywhere it appears
// (hero cards, secondary cards, chart labels) so the eye learns the mapping once.
const METRIC_META = {
  active_users: { icon: 'users', color: '#004AAD', tint: 'rgba(0,74,173,0.08)' },
  active_profiles: { icon: 'user', color: '#EA580C', tint: 'rgba(234,88,12,0.08)' },
  visits: { icon: 'eye', color: '#004AAD', tint: 'rgba(0,74,173,0.08)' },
  clicks: { icon: 'click', color: '#7C3AED', tint: 'rgba(124,58,237,0.08)' },
  leads: { icon: 'target', color: '#059669', tint: 'rgba(5,150,105,0.08)' },
  vcf: { icon: 'phone', color: '#EA580C', tint: 'rgba(234,88,12,0.08)' },
  profiles: { icon: 'users', color: '#475569', tint: 'rgba(71,85,105,0.08)' },
  created: { icon: 'rocket', color: '#004AAD', tint: 'rgba(0,74,173,0.08)' },
  edited: { icon: 'settings', color: '#7C3AED', tint: 'rgba(124,58,237,0.08)' },
  alert: { icon: 'alert', color: '#D97706', tint: 'rgba(217,119,6,0.10)' },
  interaction: { icon: 'trending', color: '#059669', tint: 'rgba(5,150,105,0.08)' },
  channel: { icon: 'globe', color: '#475569', tint: 'rgba(71,85,105,0.08)' },
};

const STATUS_META = {
  activo: { label: 'Activo', color: '#004AAD', bg: 'rgba(0,74,173,0.08)' },
  en_crecimiento: { label: 'En crecimiento', color: '#059669', bg: 'rgba(5,150,105,0.08)' },
  potencial_upgrade: { label: 'Potencial upgrade', color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
  riesgo_abandono: { label: 'Riesgo de abandono', color: '#DC2626', bg: 'rgba(220,38,38,0.08)' },
  sin_actividad: { label: 'Sin actividad', color: '#78716C', bg: 'rgba(120,113,108,0.08)' },
};

const PLATFORM_LABELS = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  twitter: 'X / Twitter',
  website: 'Sitio web',
  vcf: 'Contacto guardado',
};

const PLAN_META = {
  free: { label: 'Free', color: '#475569', bg: '#F1F5F9' },
  pro: { label: 'Pro', color: '#004AAD', bg: 'rgba(0,74,173,0.10)' },
  enterprise: { label: 'Enterprise', color: '#7C3AED', bg: 'rgba(124,58,237,0.10)' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

const formatMonth = (ym) => {
  try {
    const [y, m] = ym.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('es-CL', { month: 'short' });
  } catch {
    return ym;
  }
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.activo;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold"
      style={{ background: meta.bg, color: meta.color, fontFamily: FONT_UI, whiteSpace: 'nowrap' }}
    >
      {meta.label}
    </span>
  );
};

const PlanBadge = ({ plan }) => {
  const meta = PLAN_META[plan] || PLAN_META.free;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold"
      style={{ background: meta.bg, color: meta.color, fontFamily: FONT_UI }}
    >
      {meta.label}
    </span>
  );
};

const TrendBadge = ({ trend }) => {
  if (!trend || (trend.current === 0 && trend.previous === 0)) return null;
  const pct = trend.change_pct ?? 0;
  const isFlat = pct === 0;
  const isUp = pct > 0;
  const color = isFlat ? '#78716C' : isUp ? '#059669' : '#DC2626';
  const bg = isFlat ? 'rgba(120,113,108,0.10)' : isUp ? 'rgba(5,150,105,0.10)' : 'rgba(220,38,38,0.10)';
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 6, padding: '2px 6px', fontFamily: FONT_UI, whiteSpace: 'nowrap' }}
      title="vs. mes anterior"
    >
      {!isFlat && (isUp ? '↑' : '↓')} {Math.abs(pct)}%
    </span>
  );
};

// Small uppercase eyebrow used to group related cards/sections — gives the
// eye a resting point before each cluster instead of an unbroken wall of cards.
const SectionLabel = ({ icon, title, meta }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      {icon && <span style={{ color: '#94A3B8', display: 'flex' }}><Icon name={icon} className="w-3.5 h-3.5" /></span>}
      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.05em', fontFamily: FONT_UI }}>{title}</span>
    </div>
    {meta && <span style={{ fontSize: 11.5, color: '#CBD5E1', fontFamily: FONT_UI }}>{meta}</span>}
  </div>
);

// size: 'hero' (headline KPIs with trend) | 'compact' (supporting detail row)
const MetricCard = ({ metricKey, label, value, trend, size = 'hero' }) => {
  const meta = METRIC_META[metricKey] || METRIC_META.profiles;
  const isHero = size === 'hero';
  return (
    <div className="ci-card" style={{ padding: isHero ? '16px 18px' : '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: isHero ? 12 : 8 }}>
        <div style={{
          width: isHero ? 32 : 24, height: isHero ? 32 : 24, borderRadius: 8, background: meta.tint,
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: meta.color, flexShrink: 0,
        }}>
          <Icon name={meta.icon} className={isHero ? 'w-4 h-4' : 'w-3 h-3'} />
        </div>
        <span style={{ fontSize: isHero ? 11.5 : 10.5, fontWeight: 600, color: '#64748B', fontFamily: FONT_UI, lineHeight: 1.2 }}>{label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
        <div style={{ fontSize: isHero ? 26 : 18, fontWeight: 700, color: '#0F172A', fontFamily: FONT_UI, letterSpacing: '-0.02em' }}>{value}</div>
        <TrendBadge trend={trend} />
      </div>
    </div>
  );
};

// Small monthly bar chart — 6 buckets, one metric, no external chart lib.
const MiniTrendChart = ({ data, color = '#004AAD', height = 80 }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height, padding: '4px 2px 0' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ fontSize: 9.5, color: '#94A3B8', fontFamily: FONT_UI }}>{d.count}</div>
          <div
            style={{
              width: '100%', maxWidth: 24, borderRadius: '4px 4px 2px 2px',
              height: Math.max(3, (d.count / max) * (height - 32)),
              background: color, opacity: i === data.length - 1 ? 1 : 0.38,
              transition: 'height .3s ease',
            }}
          />
          <div style={{ fontSize: 9.5, color: '#CBD5E1', fontFamily: FONT_UI, textTransform: 'capitalize' }}>{formatMonth(d.month)}</div>
        </div>
      ))}
    </div>
  );
};

// One bordered card holding several MiniTrendCharts side by side, each with
// its own colored label — replaces N separate cards with one grouped block.
const TrendChartGroup = ({ title, charts }) => (
  <div className="ci-card" style={{ padding: '16px 18px' }}>
    <SectionLabel icon="chart" title={title} />
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${charts.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-x-6 gap-y-5`}>
      {charts.map((c) => (
        <div key={c.label} style={{ borderLeft: '2px solid #F1F5F9', paddingLeft: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11.5, fontWeight: 600, color: '#475569', fontFamily: FONT_UI }}>{c.label}</span>
          </div>
          <MiniTrendChart data={c.data} color={c.color} />
        </div>
      ))}
    </div>
  </div>
);

const Avatar = ({ name, size = 32 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#004AAD,#7C3AED)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.4, fontWeight: 700, fontFamily: FONT_UI,
  }}>
    {(name || '?')[0].toUpperCase()}
  </div>
);

const EmptyState = ({ text }) => (
  <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94A3B8', fontSize: 13, fontFamily: FONT_UI }}>{text}</div>
);

// field_changed -> icon, so the timeline reads at a glance instead of as plain text.
const activityIcon = (a) => {
  if (a.type === 'apple_wallet') return { icon: 'star', color: '#7C3AED', tint: 'rgba(124,58,237,0.10)' };
  const f = a.field_changed || '';
  if (f === 'analytics_view') return { icon: 'eye', color: '#004AAD', tint: 'rgba(0,74,173,0.10)' };
  if (f === 'analytics_export') return { icon: 'chart', color: '#059669', tint: 'rgba(5,150,105,0.10)' };
  if (f.includes('image')) return { icon: 'camera', color: '#EA580C', tint: 'rgba(234,88,12,0.10)' };
  if (['name', 'lastname'].includes(f)) return { icon: 'user', color: '#004AAD', tint: 'rgba(0,74,173,0.10)' };
  if (['position', 'company'].includes(f)) return { icon: 'briefcase', color: '#475569', tint: 'rgba(71,85,105,0.10)' };
  if (['email', 'phone', 'whatsapp', 'instagram', 'linkedin', 'facebook', 'website'].includes(f)) return { icon: 'globe', color: '#059669', tint: 'rgba(5,150,105,0.10)' };
  return { icon: 'settings', color: '#94A3B8', tint: 'rgba(148,163,184,0.10)' };
};

const ActivityTimeline = ({ activity }) => {
  if (!activity.length) return <EmptyState text="Sin actividad registrada para este perfil todavia." />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {activity.map((a, i) => {
        const meta = activityIcon(a);
        return (
          <div key={i} style={{ display: 'flex', gap: 12, padding: '11px 0', borderTop: i > 0 ? '1px solid #F1F5F9' : 'none' }}>
            <div style={{
              width: 26, height: 26, borderRadius: 8, background: meta.tint, color: meta.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Icon name={meta.icon} className="w-3 h-3" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: '#1E293B', fontFamily: FONT_UI }}>{a.description}</div>
              <div style={{ fontSize: 11.5, color: '#94A3B8', fontFamily: FONT_UI, marginTop: 1 }}>{a.relative_time}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Overview + roster ────────────────────────────────────────────────────
const OverviewSection = ({ onSelectUser }) => {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');
  const [sortBy, setSortBy] = useState('visits');
  const [page, setPage] = useState(1);
  const [overviewError, setOverviewError] = useState(false);
  const [usersError, setUsersError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/client-intelligence/overview`)
      .then((res) => setOverview(res.data))
      .catch(() => setOverviewError(true));
  }, []);

  const loadUsers = useCallback(() => {
    setLoading(true);
    setUsersError(false);
    axios.get(`${API}/admin/client-intelligence/users`, {
      params: { search, plan, status, sort_by: sortBy, page, limit: 25 },
    })
      .then((res) => {
        setUsers(res.data.users);
        setPagination(res.data.pagination);
      })
      .catch(() => setUsersError(true))
      .finally(() => setLoading(false));
  }, [search, plan, status, sortBy, page]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { setPage(1); }, [search, plan, status, sortBy]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {(overviewError || usersError) && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#B91C1C', fontFamily: FONT_UI }}>
          No se pudo conectar con el backend de Inteligencia de Clientes. Si esto es local, probablemente el backend todavia no tiene estos endpoints desplegados.
        </div>
      )}

      {overview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <SectionLabel icon="trending" title="Resumen del mes" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard metricKey="visits" label="Visitas" value={overview.this_month.visits.toLocaleString()} trend={overview.trends.visits} />
              <MetricCard metricKey="clicks" label="Clics" value={overview.this_month.clicks.toLocaleString()} trend={overview.trends.clicks} />
              <MetricCard metricKey="leads" label="Leads" value={overview.this_month.leads.toLocaleString()} trend={overview.trends.leads} />
              <MetricCard metricKey="active_profiles" label="Perfiles activos" value={overview.this_month.active_profiles} trend={overview.trends.active_profiles} />
            </div>
          </div>

          <div>
            <SectionLabel icon="clipboard-list" title="Detalle" />
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <MetricCard metricKey="active_users" label="Usuarios activos" value={overview.this_month.active_users} size="compact" />
              <MetricCard metricKey="vcf" label="Contactos guardados" value={overview.totals.vcf_downloads.toLocaleString()} size="compact" />
              <MetricCard metricKey="created" label="Perfiles creados" value={overview.this_month.profiles_created} size="compact" />
              <MetricCard metricKey="edited" label="Perfiles editados" value={overview.this_month.profiles_edited} size="compact" />
              <MetricCard metricKey="alert" label="Sin actividad" value={overview.this_month.users_no_activity} size="compact" />
            </div>
          </div>
        </div>
      )}

      <div className="ci-card">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F1F5F9' }}>
          <SectionLabel icon="users" title="Clientes" meta={pagination ? `${pagination.total} en total` : null} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 220px', minWidth: 180 }}>
              <span className="ci-filter-label">Buscar</span>
              <input
                className="ci-input"
                style={{ width: '100%' }}
                placeholder="Nombre, email o empresa..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div>
              <span className="ci-filter-label">Plan</span>
              <select className="ci-input" value={plan} onChange={(e) => setPlan(e.target.value)}>
                <option value="">Todos</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <span className="ci-filter-label">Estado</span>
              <select className="ci-input" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Todos</option>
                {Object.entries(STATUS_META).map(([key, meta]) => (
                  <option key={key} value={key}>{meta.label}</option>
                ))}
              </select>
            </div>
            <div>
              <span className="ci-filter-label">Ordenar por</span>
              <select className="ci-input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="visits">Mas visitas</option>
                <option value="clicks">Mas clics</option>
                <option value="leads">Mas leads</option>
                <option value="growth">Mayor crecimiento</option>
                <option value="profiles_count">Mas perfiles</option>
                <option value="last_activity">Ultima actividad</option>
              </select>
            </div>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="min-w-full" style={{ fontFamily: FONT_UI, borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: '#FAFBFC' }}>
                {['Usuario', 'Plan', 'Perfiles', 'Visitas', 'Clics', 'Leads', 'Crecimiento', 'Ultima actividad', 'Estado'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={9}><EmptyState text="Cargando..." /></td></tr>
              ) : usersError ? (
                <tr><td colSpan={9}><EmptyState text="No se pudo cargar el listado (ver aviso arriba)." /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={9}><EmptyState text="No se encontraron usuarios con estos filtros." /></td></tr>
              ) : users.map((u) => (
                <tr
                  key={u.id}
                  className="ci-row"
                  onClick={() => onSelectUser(u.id)}
                  style={{ borderLeft: u.status === 'riesgo_abandono' ? '3px solid #DC2626' : u.status === 'potencial_upgrade' ? '3px solid #7C3AED' : '3px solid transparent' }}
                >
                  <td className="px-4 py-3">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={u.name || u.email} size={30} />
                      <div style={{ minWidth: 0 }}>
                        <div className="text-sm font-medium text-gray-900" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || '—'}</div>
                        <div className="text-xs text-gray-500" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><PlanBadge plan={u.plan} /></td>
                  <td className="px-4 py-3 text-sm text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{u.profiles_count}</td>
                  <td className="px-4 py-3 text-sm text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{u.visits_total.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{u.clicks_total.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{u.leads_total.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm font-medium" style={{ color: u.growth_pct > 0 ? '#059669' : u.growth_pct < 0 ? '#DC2626' : '#94A3B8', fontVariantNumeric: 'tabular-nums' }}>
                    {u.growth_pct > 0 ? '↑' : u.growth_pct < 0 ? '↓' : ''} {u.growth_pct > 0 ? '+' : ''}{u.growth_pct}%
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(u.last_activity)}</td>
                  <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid #F1F5F9' }}>
            <span style={{ fontSize: 12, color: '#94A3B8', fontFamily: FONT_UI }}>
              {pagination.total} usuarios · pagina {pagination.page} de {pagination.total_pages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ci-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ opacity: page <= 1 ? 0.5 : 1 }}>Anterior</button>
              <button className="ci-btn" disabled={page >= pagination.total_pages} onClick={() => setPage((p) => p + 1)} style={{ opacity: page >= pagination.total_pages ? 0.5 : 1 }}>Siguiente</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── User detail ──────────────────────────────────────────────────────────
const UserDetailSection = ({ userId, onBack, onSelectProfile }) => {
  const [detail, setDetail] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/client-intelligence/users/${userId}`),
      axios.get(`${API}/admin/client-intelligence/users/${userId}/profiles`),
    ])
      .then(([d, p]) => {
        setDetail(d.data);
        setProfiles(p.data.profiles);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <EmptyState text="Cargando usuario..." />;
  if (!detail) return <EmptyState text="No se pudo cargar este usuario." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <button className="ci-btn" style={{ width: 'fit-content' }} onClick={onBack}>← Volver a Inteligencia de Clientes</button>

      <div className="ci-card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Avatar name={detail.user.name || detail.user.email} size={48} />
            <div>
              <h2 style={{ fontSize: 21, fontWeight: 600, color: '#0F172A', fontFamily: FONT, margin: 0 }}>{detail.user.name || detail.user.email}</h2>
              <p style={{ fontSize: 13, color: '#64748B', margin: '3px 0 0', fontFamily: FONT_UI }}>{detail.user.email}{detail.user.company ? ` · ${detail.user.company}` : ''}</p>
              <p style={{ fontSize: 12, color: '#94A3B8', margin: '3px 0 0', fontFamily: FONT_UI }}>Cliente desde {formatDate(detail.user.created_at)} · Ultima actividad {formatDate(detail.last_activity)}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <PlanBadge plan={detail.user.plan} />
            <StatusBadge status={detail.status} />
          </div>
        </div>
      </div>

      <div>
        <SectionLabel icon="chart" title="Resumen" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricCard metricKey="profiles" label="Perfiles" value={detail.totals.profiles} size="compact" />
          <MetricCard metricKey="visits" label="Visitas totales" value={detail.totals.visits.toLocaleString()} trend={detail.trends.visits} size="compact" />
          <MetricCard metricKey="clicks" label="Clics totales" value={detail.totals.clicks.toLocaleString()} size="compact" />
          <MetricCard metricKey="leads" label="Leads totales" value={detail.totals.leads.toLocaleString()} size="compact" />
          <MetricCard metricKey="vcf" label="Contactos guardados" value={detail.totals.vcf_downloads.toLocaleString()} size="compact" />
        </div>
      </div>

      <TrendChartGroup
        title="Tendencia mensual"
        charts={[
          { label: 'Visitas', color: METRIC_META.visits.color, data: detail.monthly.visits },
          { label: 'Clics', color: METRIC_META.clicks.color, data: detail.monthly.clicks },
          { label: 'Leads', color: METRIC_META.leads.color, data: detail.monthly.leads },
        ]}
      />

      <div className="ci-card">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F1F5F9' }}>
          <SectionLabel icon="users" title="Perfiles" meta={`${profiles.length} en total`} />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="min-w-full" style={{ fontFamily: FONT_UI }}>
            <thead>
              <tr style={{ background: '#FAFBFC' }}>
                {['Perfil', 'Visitas (mes)', 'Clics (mes)', 'Leads (mes)', 'Interaccion', 'Canal top', 'Ultima edicion'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {profiles.length === 0 ? (
                <tr><td colSpan={7}><EmptyState text="Este usuario todavia no tiene perfiles." /></td></tr>
              ) : profiles.map((p) => (
                <tr key={p.id} className="ci-row" onClick={() => onSelectProfile(p.id)}>
                  <td className="px-4 py-3">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={p.name || '?'} size={28} />
                      <div style={{ minWidth: 0 }}>
                        <div className="text-sm font-medium text-gray-900" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || '—'}</div>
                        <div className="text-xs text-gray-500" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.position}{p.position && p.company ? ' · ' : ''}{p.company}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.visits_month}</td>
                  <td className="px-4 py-3 text-sm text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.clicks_month}</td>
                  <td className="px-4 py-3 text-sm text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.leads_month}</td>
                  <td className="px-4 py-3 text-sm text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.engagement_rate}%</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{p.top_channel ? (PLATFORM_LABELS[p.top_channel] || p.top_channel) : '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(p.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Profile detail ───────────────────────────────────────────────────────
const ProfileDetailSection = ({ profileId, onBackToUser }) => {
  const [detail, setDetail] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/client-intelligence/profiles/${profileId}`),
      axios.get(`${API}/admin/client-intelligence/profiles/${profileId}/activity`),
    ])
      .then(([d, a]) => {
        setDetail(d.data);
        setActivity(a.data.activity);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [profileId]);

  if (loading) return <EmptyState text="Cargando perfil..." />;
  if (!detail) return <EmptyState text="No se pudo cargar este perfil." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <button className="ci-btn" style={{ width: 'fit-content' }} onClick={() => onBackToUser(detail.profile.user_id)}>← Volver al usuario</button>

      <div className="ci-card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar name={detail.profile.name} size={48} />
          <div>
            <h2 style={{ fontSize: 21, fontWeight: 600, color: '#0F172A', fontFamily: FONT, margin: 0 }}>{detail.profile.name}</h2>
            <p style={{ fontSize: 13, color: '#64748B', margin: '3px 0 0', fontFamily: FONT_UI }}>
              {detail.profile.position}{detail.profile.position && detail.profile.company ? ' · ' : ''}{detail.profile.company} · /pages/{detail.profile.slug}
            </p>
            <p style={{ fontSize: 12, color: '#94A3B8', margin: '3px 0 0', fontFamily: FONT_UI }}>
              Ultima interaccion: {detail.last_interaction ? formatDate(detail.last_interaction) : 'Sin interacciones'} · Ultima edicion: {formatDate(detail.profile.updated_at)}
            </p>
          </div>
        </div>
      </div>

      <div>
        <SectionLabel icon="calendar" title="Este mes" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard metricKey="visits" label="Visitas" value={detail.this_month.visits} trend={detail.trends.visits} />
          <MetricCard metricKey="clicks" label="Clics" value={detail.this_month.clicks} />
          <MetricCard metricKey="leads" label="Leads" value={detail.this_month.leads} />
          <MetricCard metricKey="vcf" label="Contactos guardados" value={detail.this_month.vcf_downloads} />
        </div>
      </div>

      <div>
        <SectionLabel icon="target" title="Calidad e historico" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard metricKey="interaction" label="Tasa de interaccion" value={`${detail.this_month.engagement_rate}%`} size="compact" />
          <MetricCard metricKey="interaction" label="Conversion a contacto" value={`${detail.this_month.vcf_conversion_rate}%`} size="compact" />
          <MetricCard metricKey="channel" label="Canal mas usado" value={detail.top_channel ? (PLATFORM_LABELS[detail.top_channel] || detail.top_channel) : '—'} size="compact" />
          <MetricCard metricKey="profiles" label="Visitas totales" value={detail.totals.visits.toLocaleString()} size="compact" />
        </div>
      </div>

      <TrendChartGroup
        title="Tendencia mensual"
        charts={[
          { label: 'Visitas', color: METRIC_META.visits.color, data: detail.monthly.visits },
          { label: 'Clics', color: METRIC_META.clicks.color, data: detail.monthly.clicks },
          { label: 'Contactos', color: METRIC_META.vcf.color, data: detail.monthly.vcf_downloads },
          { label: 'Leads', color: METRIC_META.leads.color, data: detail.monthly.leads },
        ]}
      />

      {detail.click_breakdown.length > 0 && (
        <div className="ci-card" style={{ padding: '16px 18px' }}>
          <SectionLabel icon="globe" title="Clics por canal" meta="total historico" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {detail.click_breakdown.slice(0, 6).map((c) => {
              const max = detail.click_breakdown[0].count;
              return (
                <div key={c.platform} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 110, fontSize: 12, color: '#475569', fontFamily: FONT_UI, flexShrink: 0 }}>{PLATFORM_LABELS[c.platform] || c.platform}</span>
                  <div style={{ flex: 1, height: 7, borderRadius: 99, background: '#F1F5F9', overflow: 'hidden' }}>
                    <div style={{ width: `${(c.count / max) * 100}%`, height: '100%', background: '#004AAD', borderRadius: 99 }} />
                  </div>
                  <span style={{ fontSize: 12, color: '#1E293B', fontWeight: 600, fontFamily: FONT_UI, width: 30, textAlign: 'right' }}>{c.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="ci-card" style={{ padding: '16px 18px' }}>
        <SectionLabel icon="bell" title="Actividad del perfil" meta="del dueño, mas reciente primero" />
        <ActivityTimeline activity={activity} />
      </div>
    </div>
  );
};

// ── Root ──────────────────────────────────────────────────────────────────
// initialView/initialUserId/initialProfileId only exist so the local preview
// harness (__PreviewClientIntelligence.js) can deep-link into a view for
// screenshotting — the real admin panel always mounts with the defaults.
const ClientIntelligenceTab = ({ initialView = 'overview', initialUserId = null, initialProfileId = null }) => {
  const [view, setView] = useState(initialView);
  const [selectedUserId, setSelectedUserId] = useState(initialUserId);
  const [selectedProfileId, setSelectedProfileId] = useState(initialProfileId);

  return (
    <div>
      <style>{FONT_FACE}</style>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 300, color: '#0F172A', letterSpacing: '-0.02em', margin: 0, fontFamily: FONT }}>Inteligencia de Clientes</h1>
        <p style={{ fontSize: 13, color: '#64748B', margin: '6px 0 0', fontFamily: FONT_UI }}>
          Uso interno — comportamiento de clientes de Contact, no visible para usuarios finales.
        </p>
      </div>

      {view === 'overview' && (
        <OverviewSection
          onSelectUser={(id) => { setSelectedUserId(id); setView('user'); }}
        />
      )}
      {view === 'user' && selectedUserId && (
        <UserDetailSection
          userId={selectedUserId}
          onBack={() => setView('overview')}
          onSelectProfile={(id) => { setSelectedProfileId(id); setView('profile'); }}
        />
      )}
      {view === 'profile' && selectedProfileId && (
        <ProfileDetailSection
          profileId={selectedProfileId}
          onBackToUser={(userId) => { setSelectedUserId(userId); setView('user'); }}
        />
      )}
    </div>
  );
};

export default ClientIntelligenceTab;
